import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import type { Category, Insight } from '../types/api';
import { Pagination, Reveal } from '../components/ui';
import { ExploreMore, InsightFilters, InsightsHero, type FeaturedInsight } from '../components/insights';

const PAGE_SIZE = 9;
const time = (i: Insight) => Date.parse(i.created_at) || 0;

function InsightCard({ insight, category, delay }: { insight: Insight; category: string; delay: number }) {
  return (
    <Reveal delay={delay}>
      <Link to={`/blog/${insight.id}`} className="group block">
        <div className="relative aspect-[3/2] overflow-hidden bg-secondary-200">
          {insight.image?.url && <img src={insight.image.url} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />}
          <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-brand transition-transform duration-500 ease-out group-hover:scale-x-100" />
        </div>
        <div className="mt-5 text-xs font-normal uppercase tracking-[0.0625rem] text-ink">{category}</div>
        <h3 className="mt-3 font-sans text-[1.375rem] font-bold normal-case leading-snug tracking-normal text-black transition-colors group-hover:text-brand-dark">{insight.title}</h3>
      </Link>
    </Reveal>
  );
}

export default function Blog() {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const gridTop = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([apiService.getInsights(), apiService.getCategories().catch(() => [] as Category[])])
      .then(([ins, cats]) => { if (alive) { setInsights([...ins].sort((a, b) => time(b) - time(a))); setCategories(cats); setStatus('ok'); } })
      .catch(() => alive && setStatus('error'));
    return () => { alive = false; };
  }, []);

  const catName = useMemo(() => new Map(categories.map((c) => [c.id, c.title])), [categories]);
  const labelOf = (i: Insight) => (i.category_id && catName.get(i.category_id)) || 'Actualité';

  const featured: FeaturedInsight[] = useMemo(
    () => insights.slice(0, 3).map((i) => ({ id: i.id, title: i.title, category: labelOf(i), image: i.image?.url, to: `/blog/${i.id}` })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [insights, catName],
  );

  const options = useMemo(
    () => categories.map((c) => ({ id: c.id, label: c.title, count: insights.filter((i) => i.category_id === c.id).length })),
    [categories, insights],
  );

  const filtered = useMemo(() => (selected.length === 0 ? insights : insights.filter((i) => i.category_id && selected.includes(i.category_id))), [insights, selected]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const toggle = (id: string) => { setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])); setPage(1); };
  const goPage = (n: number) => { setPage(n); gridTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  return (
    <div id="content">
      <InsightsHero items={featured} />

      <section ref={gridTop} className="scroll-mt-28 bg-white py-14 text-black md:py-20">
        <div className="mx-auto max-w-[90rem] px-6 lg:px-16">
          <div className={options.length > 0 ? 'grid gap-10 lg:grid-cols-[18rem_1fr] lg:gap-14' : ''}>
            {options.length > 0 && (
              <aside aria-label="Filtres">
                <button onClick={() => setFiltersOpen((v) => !v)} aria-expanded={filtersOpen} className="mb-4 flex w-full items-center justify-between border-b border-line pb-3 font-display text-xl font-bold lg:hidden">
                  Filtrer par thème<span aria-hidden>{filtersOpen ? '−' : '+'}</span>
                </button>
                <div className={filtersOpen ? 'block' : 'hidden lg:block'}>
                  <InsightFilters title="Thèmes" options={options} selected={selected} onToggle={toggle} onClear={() => { setSelected([]); setPage(1); }} />
                </div>
              </aside>
            )}

            <div>
              {status === 'loading' && (
                <div className="grid gap-x-8 gap-y-12 md:grid-cols-2 xl:grid-cols-3" aria-busy>
                  {[0, 1, 2].map((n) => <div key={n} className="aspect-[3/2] animate-pulse bg-secondary-200" />)}
                </div>
              )}
              {status === 'error' && <p className="py-10 text-lg text-ink">Impossible de charger les actualités pour le moment. Réessayez dans un instant.</p>}
              {status === 'ok' && visible.length === 0 && <p className="py-10 text-lg text-ink">Aucune actualité ne correspond à ces filtres.</p>}
              {visible.length > 0 && (
                <div className="grid gap-x-8 gap-y-14 md:grid-cols-2 xl:grid-cols-3">
                  {visible.map((i, n) => <InsightCard key={i.id} insight={i} category={labelOf(i)} delay={(n % 3) * 0.08} />)}
                </div>
              )}
              {status === 'ok' && <Pagination page={current} pages={pages} total={filtered.length} pageSize={PAGE_SIZE} onChange={goPage} />}
            </div>
          </div>
        </div>
      </section>

      <ExploreMore />
    </div>
  );
}
